import { Dispatch, FormEvent, SetStateAction, useCallback, useContext, useEffect, useState } from "react";

import { InteractionObjectType, InteractionType, ProjectIssuanceConstants } from "../../constants";
import {
  PillData,
  ProjectIssuanceBNGPanelData,
  ProjectIssuanceCarbonPanelData,
  ProjectIssuanceNNPanelData,
  ProjectIssuanceSpecificSchema,
  SelectData,
} from "../../models";
import { ProjectContext } from "../../route/shared/projects/project/ProjectContext";
import { addOrRemoveObjectInteractions } from "../../service/interaction";
import { createProjectIssuance, updateProjectIssuance } from "../../service/project/ProjectService.full";
import {
  getStandardSpecificSchemaDetails,
  searchInteractions,
  searchIssuanceTransactions,
  searchVerifiers,
} from "../../service/query";
import { ServiceError, Status } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { useIsLoadingWrapper } from "../../utils";
import { dataGridMapFilterCriteria, Toast } from "../../widget";
import { InteractionTag } from "../TagsPanel/models";
import { getQuantityTypeErrorMessage, QUANTITY_TYPE_INVALID_ERROR_CODE } from "./components/IssuancePanelDynamic/utils";
import { getDefaultIssuanceForMeasurement, parseIssuanceSchema, reorderIssuanceSchemaProperties } from "./utils";

interface UseIssuancePanelProps {
  issuance?: ProjectIssuanceBNGPanelData | ProjectIssuanceCarbonPanelData | ProjectIssuanceNNPanelData;
  onClose: () => void;
  handleRefresh: () => void;
}

interface UseIssuancePanelReturnData {
  errors: ServiceError[] | undefined;
  issuanceSchema: ProjectIssuanceSpecificSchema | undefined;

  tagPills: PillData[];
  changedTagsExternal: InteractionTag[];
  setChangedTagsExternal: Dispatch<SetStateAction<InteractionTag[]>>;

  standardSpecificIssuanceData:
    | ProjectIssuanceCarbonPanelData
    | ProjectIssuanceBNGPanelData
    | ProjectIssuanceNNPanelData;
  setStandardSpecificIssuanceData: Dispatch<
    SetStateAction<ProjectIssuanceCarbonPanelData | ProjectIssuanceBNGPanelData | ProjectIssuanceNNPanelData>
  >;

  showTagsPanel: boolean;
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;

  setRefreshTags: Dispatch<SetStateAction<boolean>>;

  isHandleSubmitLoading: boolean;
  handleClosePanel: () => void;
  handleSubmit: () => Promise<void>;
  handleSubmitForm: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  showIssuanceTransactionWarning: boolean;
  setShowIssuanceTransactionWarning: Dispatch<SetStateAction<boolean>>;
  issuanceTransactionCount: number;

  verifierData: SelectData;
}

export const useIssuancePanel = ({
  issuance,
  onClose,
  handleRefresh,
}: UseIssuancePanelProps): UseIssuancePanelReturnData => {
  const { currentOrganisationUuid } = useAuth();
  const { projectDetails } = useContext(ProjectContext);
  const projectUnitMeasurement = projectDetails?.unitMeasurements?.[0];

  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const [showTagsPanel, setShowTagsPanel] = useState(false);

  const [issuanceSchema, setIssuanceSchema] = useState<ProjectIssuanceSpecificSchema>();
  const [standardSpecificIssuanceData, setStandardSpecificIssuanceData] = useState<
    ProjectIssuanceCarbonPanelData | ProjectIssuanceBNGPanelData | ProjectIssuanceNNPanelData
  >(getDefaultIssuanceForMeasurement(projectUnitMeasurement));

  const [tagPills, setTagPills] = useState<PillData[]>([]);
  const [changedTagsExternal, setChangedTagsExternal] = useState<InteractionTag[]>([]);
  const [refreshTags, setRefreshTags] = useState(false);

  const [showIssuanceTransactionWarning, setShowIssuanceTransactionWarning] = useState(false);
  const [issuanceTransactionCount, setIssuanceTransactionCount] = useState(0);

  const [verifierData, setVerifierData] = useState<SelectData>([]);

  const handleClosePanel = (): void => {
    onClose();
    setStandardSpecificIssuanceData(getDefaultIssuanceForMeasurement(projectUnitMeasurement));
    setTagPills([]);
    setChangedTagsExternal([]);
    setErrors([]);
    setShowIssuanceTransactionWarning(false);
  };

  const checkIssuanceTransactions = useCallback(
    async (uuid: string): Promise<boolean> => {
      const filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.issuanceUuid = {
        operator: "eq",
        value: uuid,
      };

      const res = await searchIssuanceTransactions({
        paging: {
          limit: 1,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: { results: filterCriteria },
      });

      if (res.status === Status.Success && res.data?.results.length && res.data.results.length > 0) {
        setIssuanceTransactionCount(res.data.paging.total);
        setShowIssuanceTransactionWarning(true);
        return true;
      }
      return false;
    },
    [issuance?.uuid]
  );

  const handleSubmit = useIsLoadingWrapper(async (): Promise<void> => {
    setErrors([]);
    if (!projectDetails?.uuid) return;

    if (issuance?.uuid && !showIssuanceTransactionWarning) {
      if (await checkIssuanceTransactions(issuance?.uuid)) return;
    }

    // The date field is hidden unless the issuance is issued, so a date picked before the status
    // changed is not sent - the api rejects a draft carrying one.
    const issuanceDate =
      standardSpecificIssuanceData.status === ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED
        ? standardSpecificIssuanceData.issuanceDate
        : null;

    const res =
      issuance === undefined
        ? await createProjectIssuance({
            projectUuid: projectDetails?.uuid,
            price: standardSpecificIssuanceData.price,
            priceCurrency: "GBP",
            status: standardSpecificIssuanceData.status ?? "",
            quantityType: standardSpecificIssuanceData.quantityType ?? "",
            issuanceDate,
            serialNumber: standardSpecificIssuanceData.serialNumber,
            availableQuantity: standardSpecificIssuanceData.availableQuantity ?? 0,
            reservedQuantity: standardSpecificIssuanceData.reservedQuantity ?? 0,
            soldQuantity: standardSpecificIssuanceData.soldQuantity ?? 0,
            totalQuantity:
              (standardSpecificIssuanceData.availableQuantity ?? 0) +
              (standardSpecificIssuanceData.reservedQuantity ?? 0) +
              (standardSpecificIssuanceData.soldQuantity ?? 0),
            standardSpecificIssuanceData: standardSpecificIssuanceData.standardSpecificIssuanceData,
            verifierUuid: standardSpecificIssuanceData.verifierUuid,
            validateOnly: false,
          })
        : await updateProjectIssuance({
            uuid: standardSpecificIssuanceData.uuid ?? "",
            price: standardSpecificIssuanceData.price,
            priceCurrency: "GBP",
            status: standardSpecificIssuanceData.status ?? "",
            quantityType: standardSpecificIssuanceData.quantityType ?? "",
            issuanceDate,
            serialNumber: standardSpecificIssuanceData.serialNumber,
            availableQuantity: standardSpecificIssuanceData.availableQuantity ?? 0,
            // BR-02902: sold and reserved are not amendable after creation. The server carries the
            // stored values through and recomputes the total, so the update payload omits them.
            totalQuantity:
              (standardSpecificIssuanceData.availableQuantity ?? 0) +
              (standardSpecificIssuanceData.reservedQuantity ?? 0) +
              (standardSpecificIssuanceData.soldQuantity ?? 0),
            standardSpecificIssuanceData: standardSpecificIssuanceData.standardSpecificIssuanceData,
            rowVersion: standardSpecificIssuanceData.rowVersion ?? 0,
            verifierUuid: standardSpecificIssuanceData.verifierUuid,
          });

    if (res.status === Status.Success) {
      if (res.status === Status.Success && res.data !== undefined) {
        if (issuance === undefined && changedTagsExternal !== undefined && changedTagsExternal.length > 0) {
          await addOrRemoveObjectInteractions({
            objectInteractions:
              changedTagsExternal
                .filter((tag) => tag.checked)
                .map((tag) => ({
                  interactionUuid: tag.uuid,
                  objectType: InteractionObjectType.ISSUANCE,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  objectUuid: res.data!.uuid,
                  operation: "add",
                })) ?? [],
          });
        }
      }
      Toast.success({ message: "Issuance created successfully" });
      handleRefresh();
      handleClosePanel();
    }

    if (res.status === Status.Error) {
      /*
        The server names the rejected field by its column, `quantityType`, whatever the standard calls
        it - so a Nutrient Neutrality project saved without a nutrient came back talking about a unit
        type. Renaming only this code keeps every other message exactly as the service produced it.

        The wording itself lives with the label, in getQuantityTypeErrorMessage.
      */
      const quantityTypeMessage = getQuantityTypeErrorMessage(
        issuanceSchema,
        standardSpecificIssuanceData.quantityType
      );

      setErrors(
        res.errors?.map((error) =>
          error.code === QUANTITY_TYPE_INVALID_ERROR_CODE ? { ...error, message: quantityTypeMessage } : error
        )
      );
    }
  }, setIsHandleSubmitLoading);

  const handleSubmitForm = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await handleSubmit();
  };

  const getAndSetIssuanceSchema = useCallback(
    async (uuid: string): Promise<void> => {
      const result = await getStandardSpecificSchemaDetails({ standardSpecificSchemaUuid: uuid });
      if (result.status === Status.Success && result.data) {
        const parsedSchema = parseIssuanceSchema(result.data.issuanceSchema);

        setIssuanceSchema(
          parsedSchema && {
            ...parsedSchema,
            properties: reorderIssuanceSchemaProperties(parsedSchema),
          }
        );
      }
    },
    [projectDetails?.standardSpecificSchemaUuid]
  );

  const fetchTags = useCallback(
    async (uuid: string, orgUuid: string): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.type = {
        operator: "eq",
        value: InteractionType.TAG,
      };
      filterCriteria.objectType = {
        operator: "eq",
        value: InteractionObjectType.ORGANISATION,
      };
      filterCriteria.objectUuid = {
        operator: "eq",
        value: orgUuid,
      };
      filterCriteria.objectInteractions = {
        some: {
          objectUuid: {
            operator: "eq",
            value: uuid,
          },
          endDate: {
            operator: "eq",
            value: null,
          },
        },
      };
      await searchInteractions({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 50,
        },
        filter: { results: filterCriteria },
      }).then((response) => {
        if (response.data?.results != null) {
          setTagPills(
            response.data.results.map(
              (x) =>
                ({
                  key: x.uuid,
                  label: JSON.parse(x.data).name,
                }) as PillData
            )
          );
        }
      });
    },
    [issuance?.uuid, currentOrganisationUuid, refreshTags]
  );

  const getAndSetVerifiers = useCallback(
    async (uuid: string): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.projectTypes = {
        some: {
          uuid: {
            operator: "eq",
            value: uuid,
          },
        },
      };

      await searchVerifiers({
        filter: {
          results: filterCriteria,
        },
        paging: {
          limit: 10,
          beforeCursor: null,
          afterCursor: null,
        },
        sort: [],
      }).then((response) => {
        const valData =
          response.data?.results?.map((x) => {
            return { key: x.uuid, value: x.displayName };
          }) ?? [];
        setVerifierData(valData);
      });
    },
    [projectDetails?.projectType.uuid]
  );

  useEffect(() => {
    if (projectDetails?.standardSpecificSchemaUuid) {
      getAndSetIssuanceSchema(projectDetails.standardSpecificSchemaUuid);
    }
    if (projectDetails?.projectType.uuid) {
      getAndSetVerifiers(projectDetails.projectType.uuid);
    }
  }, [projectDetails?.standardSpecificSchemaUuid, projectDetails?.projectType.uuid]);

  useEffect(() => {
    setStandardSpecificIssuanceData(issuance ?? getDefaultIssuanceForMeasurement(projectUnitMeasurement));
  }, [issuance?.uuid, projectUnitMeasurement]);

  useEffect(() => {
    if (issuance?.uuid && currentOrganisationUuid) {
      fetchTags(issuance.uuid, currentOrganisationUuid);
    } else {
      setTagPills(changedTagsExternal.filter((x) => x.checked).map((x) => ({ key: x.uuid, label: x.data.name })));
    }
  }, [issuance?.uuid, currentOrganisationUuid, refreshTags, changedTagsExternal]);

  return {
    errors,
    issuanceSchema,

    standardSpecificIssuanceData,
    setStandardSpecificIssuanceData,

    showTagsPanel,
    setShowTagsPanel,

    tagPills,
    changedTagsExternal,
    setChangedTagsExternal,

    setRefreshTags,

    isHandleSubmitLoading,
    handleClosePanel,
    handleSubmit,
    handleSubmitForm,
    showIssuanceTransactionWarning,
    setShowIssuanceTransactionWarning,
    issuanceTransactionCount,
    verifierData,
  };
};
